import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Form, Input, Select, Switch, Button, Card, InputNumber } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import {
  nonNegativeIntegerValidator,
  noWhitespaceValidator,
} from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

function PlanTypeEdit() {
  const { plan_type_id } = useParams();
  const { setPageTitle } = useCommon();
  const navigate = useNavigate();
  const notify = useNotify();
  const [form] = Form.useForm();
  const [languages, setLanguages] = useState([]);

  const getLanguages = async () => {
    const response = await masterService.getVideoLanguages({
      isAll: true,
      status: "Y",
    });
    console.log("Languages:", response);
    const data = response?.data?.data || response?.data || [];
    const list = data.map((item) => ({
      value: item.lang_id,
      label: item.lang_name,
    }));
    setLanguages(list);
  };

  const getPlanTypeDetails = async () => {
    const response = await masterService.getSinglePlanType(plan_type_id);
    const data = response.data.data;
    form.setFieldsValue(data);
    console.log("Plan Type Details:", data);
  };

  useEffect(() => {
    setPageTitle(plan_type_id === "new" ? "Add Plan Type" : "Edit Plan Type");
    if (plan_type_id !== "new") {
      getPlanTypeDetails();
    }
    getLanguages();
  }, [plan_type_id, setPageTitle]);

  const onFinish = async (values) => {
    try {
      if (plan_type_id === "new") {
        const response = await masterService.addPlanType(values);
        if (response.data.status) {
          navigate("/plan-types");
        } else {
          notify.error({
            message: response.data.message || "Failed to add plan type",
          });
        }
        console.log("Response:", response);
      } else {
        const response = await masterService.updatePlanType(
          plan_type_id,
          values
        );
        if (response.data.status) {
          navigate("/plan-types");
        } else {
          notify.error({
            message: response.data.message || "Failed to update plan type",
          });
        }
        console.log("Response:", response);
      }
    } catch (error) {
      console.log("Error:", error);
      notify.error({
        message: error?.data?.message || "Failed to add or update plan type",
      });
    }
    console.log("Form Values:", values);
  };

  return (
    <div>
      <Card title="Plan Type Form" className="">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ is_active: true }}
        >
          {/* Name */}
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: "Please enter plan type name" },
              { validator: noWhitespaceValidator },
            ]}
          >
            <Input placeholder="Enter plan type name" />
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label="Description"
            rules={[{ validator: noWhitespaceValidator }]}
          >
            <Input placeholder="Enter plan type description" />
          </Form.Item>

          <div className="grid grid-cols-3 gap-4">
            {/* Order Number */}
            <Form.Item
              name="order_number"
              label="Order Number"
              rules={[nonNegativeIntegerValidator]}
            >
              <InputNumber placeholder="Enter order number" />
            </Form.Item>

            {/* Is Active */}
            <Form.Item
              name="is_active"
              label="Is Active"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
            name="apple_store_id"
            label="apple_store_id"
            rules={[
              { required: true, message: "Please enter apple_store_id" },
              { validator: noWhitespaceValidator },
            ]}
          >
            <Input placeholder="Enter apple_store_id" />
          </Form.Item>
          </div>

          {/* Multilingual Data */}
          <Form.List name="multilingual_data">
            {(fields, { add, remove }) => (
              <>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-semibold">Multilingual Data</h3>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    Add Plan Type Data
                  </Button>
                </div>

                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    className="mb-3 border rounded-md"
                    title={`Language Entry #${name + 1}`}
                    extra={
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        style={{ color: "red" }}
                      />
                    }
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "fk_lang_id"]}
                      label="Language"
                      rules={[
                        { required: true, message: "Please enter language" },
                      ]}
                    >
                      <Select
                        placeholder="Select language"
                        options={languages}
                        showSearch
                        filterOption={(input, option) =>
                          option.label
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "name"]}
                      label="Translated Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter translated name",
                        },
                        {
                          validator: noWhitespaceValidator,
                        },
                      ]}
                    >
                      <Input placeholder="Enter translated name" />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, "description"]}
                      label="Translated Description"
                      rules={[{ validator: noWhitespaceValidator }]}
                    >
                      <Input placeholder="Enter translated description" />
                    </Form.Item>
                  </Card>
                ))}
              </>
            )}
          </Form.List>

          {/* Submit */}
          <Form.Item>
            <Button type="primary" htmlType="submit" className="mt-4">
              {plan_type_id === "new" ? "Create" : "Update"}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

export default PlanTypeEdit;
